import React, { useState, useEffect, useRef, useDeferredValue } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "../../components/ui/Button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Card } from "../../components/ui/Card";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import {
  GetClientDto,
  ContactPersonDto,
  ClientDocumentDto,
} from "../../types/client.types";
import { Invoice, InvoiceStatusEnum } from "../../types/invoice.types";
import { StatusBadge } from "../../components/ui/StatusBadge";
import {
  getClientById,
  deleteClient,
} from "../../service/client/ClientServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheck,
  faEye,
  faPencil,
  faPlus,
  faTrash,
  faXmark,
  faDownload,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { getInvoiceByClientId } from "../../service/invoice/InvoiceServices";
import { PaginationInputDto } from "../../model/common/PaginatedInputDto";


export const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const [client, setClient] = useState<GetClientDto | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [isDeletingClient, setIsDeletingClient] = useState<boolean>(false);
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  const [outstandingBalance, setOutstandingBalance] = useState<number>(0);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [lazyParams, setLazyParams] = useState({
    first: 1,
    rows: 5,
    page: 1,
    sortField: undefined,
    sortOrder: undefined
  });

  const fetchClientData = async () => {
      if (!id) return;
      const response = await getClientById(dispatch, id);

      if (response) {
        setClient(response);
      }

    }

  useEffect(() => {
    fetchClientData();
  }, [id]);

  useEffect(() => {
    const fetchClientInvoiceData = async () => {
      if (!id) return;
      const invoiceParams: PaginationInputDto = {
        Limit: lazyParams.rows,
        Page: lazyParams.first,
        SortProperty: lazyParams?.sortField || "issue_date",
        SortBy: lazyParams?.sortOrder === 1 ? "ASC" : "DESC",
        Filter: undefined
      }
      const invoicesByClients = await getInvoiceByClientId(dispatch, invoiceParams, parseInt(id));

      // TODO: In a real app, we would fetch the client's invoices here
      // For now, using mock data
      if (invoicesByClients) {
        setInvoices(invoicesByClients?.List || []);
        setTotalRecords(invoicesByClients?.TotalCount || 0);
      }

      // Calculate total revenue and outstanding balance
      const total = invoicesByClients?.List?.reduce(
        (sum: any, invoice: any) => sum + Number(invoice.total),
        0
      );
      const outstanding = invoicesByClients?.List
        ?.filter(
          (invoice: any) => invoice.status === InvoiceStatusEnum.OVERDUE
        )
        .reduce((sum: any, invoice: any) => sum + Number(invoice.total), 0);

      setTotalRevenue(total);
      setOutstandingBalance(outstanding);

    }

    fetchClientInvoiceData();
  }, [lazyParams, id]);

  const handleDeleteClient = async () => {
    if (!id) return;

    try {
      setIsDeletingClient(true);
      const response = await deleteClient(dispatch, id);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Client deleted successfully",
          life: 3000,
        });

        setDeleteDialogVisible(false);

        // Navigate back to client list after a short delay
        setTimeout(() => {
          navigate("/client");
        }, 1500);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete client",
          life: 3000,
        });
        setDeleteDialogVisible(false);
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "An error occurred while deleting the client",
        life: 3000,
      });
    } finally {
      setIsDeletingClient(false);
    }
  };
  const onPage = (event: any) => {
    setLazyParams(prev => ({
      ...prev,
      first: event.first + 1,
      rows: event.rows,
    }));
  };
  const formatCurrency = (value: number) => {
    return "$" + value?.toFixed(2);
  };

  // Column templates for invoices table
  const invoiceNumberTemplate = (rowData: Invoice) => {
    return (
      <a
        href={`/invoice/${rowData.invoice_id}`}
        className="text-primary hover:underline font-medium"
        onClick={(e) => {
          e.preventDefault();
          navigate(`/invoice/${rowData.invoice_id}`);
        }}
      >
        {rowData.invoice_number}
      </a>
    );
  };

  const statusTemplate = (rowData: Invoice) => {
    return <StatusBadge status={rowData.status} />;
  };

  const amountTemplate = (rowData: Invoice) => {
    return <span className="font-medium">{formatCurrency(Number(rowData?.total))}</span>;
  };

  const dateTemplate = (rowData: Invoice, field: string) => {
    if (!rowData[field as keyof Invoice]) return "-";
    const date = new Date(rowData[field as keyof Invoice] as string);
    return date.toLocaleDateString();
  };

  const actionTemplate = (rowData: Invoice) => {
    return (
      <div className="flex gap-2 justify-center">
        <Button
          className="p-button-rounded p-button-text"
          onClick={() => navigate(`/invoice/${rowData.invoice_id}`)}
        >
          <FontAwesomeIcon icon={faEye} /> View
        </Button>
        <Button
          className="p-button-rounded p-button-text"
          onClick={() => navigate(`/invoice/edit/${rowData.invoice_id}`)}
        >
          <FontAwesomeIcon icon={faPencil} /> Edit
        </Button>
      </div>
    );
  };

  // Contact person list view
  const renderContactPersons = () => {
    if (!client?.contact_persons || client.contact_persons.length === 0) {
      return (
        <div className="text-gray-500 text-center py-4">
          No contact persons found for this client.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {client.contact_persons.map(
          (person: ContactPersonDto, index: number) => (
            <Card key={index} className="p-0">
              <div className="p-3">
                <h3 className="text-lg font-semibold mb-2">
                  {person.first_name} {person.last_name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {person.email && (
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="text-gray-500 mr-2"
                      />
                      <a
                        href={`mailto:${person.email}`}
                        className="text-primary hover:underline"
                      >
                        {person.email}
                      </a>
                    </div>
                  )}
                  {person.phone && (
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="text-gray-500 mr-2"
                      />
                      <span>{person.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )
        )}
      </div>
    );
  };

  // Document list view
  const renderDocuments = () => {
    if (!client?.documents || client.documents.length === 0) {
      return (
        <div className="text-gray-500 text-center py-4">
          No documents found for this client.
        </div>
      );
    }

    // Function to handle document download
    const handleDocumentDownload = (doc: ClientDocumentDto) => {
      if (doc.base64Content) {
        // Handle base64 content download
        try {
          // Get proper file extension and name
          const fileName =
            doc.file_name || `Document_${doc.id || new Date().getTime()}`;
          const fileExt = fileName.split(".").pop()?.toLowerCase() || "pdf";
          const finalFileName = fileName.includes(".")
            ? fileName
            : `${fileName}.${fileExt}`;

          // Process base64 content - remove data URI prefix if present
          let base64Data = doc.base64Content;
          if (base64Data.includes(";base64,")) {
            base64Data = base64Data.split(";base64,")[1];
          }

          // Determine MIME type
          let mimeType = doc.file_type || "application/pdf";
          if (finalFileName.toLowerCase().endsWith(".pdf")) {
            mimeType = "application/pdf";
          } else if (
            finalFileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)
          ) {
            mimeType = `image/${fileExt}`;
          }

          // Convert base64 to blob - use Uint8Array directly for binary files
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], { type: mimeType });

          // Create and trigger download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = finalFileName;
          document.body.appendChild(a);
          a.click();

          // Cleanup
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);

          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: `${finalFileName} downloaded successfully`,
            life: 3000,
          });
        } catch (error) {
          console.error("Error downloading document:", error);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to download document. Check console for details.",
            life: 3000,
          });
        }
      } else if (doc.file_path) {
        // Handle file_path download
        window.open(doc.file_path, "_blank");
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "No document content available to download",
          life: 3000,
        });
      }
    };

    return (
      <div className="space-y-2">
        {client.documents.map((doc: ClientDocumentDto, index: number) => {
          const fileName =
            doc.file_name ||
            (doc.file_path
              ? doc.file_path.split("/").pop()
              : `Document ${index + 1}`);
          const fileExtension = fileName?.split(".").pop()?.toLowerCase() || "";

          // Determine file type icon
          let fileIcon = faDownload;
          if (["pdf"].includes(fileExtension)) {
            fileIcon = faDownload;
          } else if (["jpg", "jpeg", "png", "gif"].includes(fileExtension)) {
            fileIcon = faDownload;
          }

          return (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
            >
              <span className="flex items-center">
                <FontAwesomeIcon
                  icon={fileIcon}
                  className="text-gray-500 mr-2"
                />
                {fileName}
              </span>
              <div className="flex gap-2">
                <Button
                  className="p-button-sm p-button-text"
                  onClick={() => handleDocumentDownload(doc)}
                >
                  <FontAwesomeIcon icon={faDownload} /> Download
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAddresses = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Billing Address */}
        <Card className="p-0">
          <div className="p-3">
            <h3 className="text-lg font-semibold flex items-center mb-2">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="text-gray-500 mr-2"
              />
              Billing Address
            </h3>
            <div className="space-y-1 text-gray-700">
              {client?.billing_address?.address_line1 && (
                <p>{client.billing_address.address_line1}</p>
              )}
              {client?.billing_address?.address_line2 && (
                <p>{client.billing_address.address_line2}</p>
              )}
              <p>
                {[
                  client?.billing_address?.city,
                  client?.billing_address?.state,
                  client?.billing_address?.postal_code,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              {client?.billing_address?.country_id && (
                <p>Country ID: {client.billing_address.country_id}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Shipping Address */}
        <Card className="p-0">
          <div className="p-3">
            <h3 className="text-lg font-semibold flex items-center mb-2">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="text-gray-500 mr-2"
              />
              Shipping Address
            </h3>
            <div className="space-y-1 text-gray-700">
              {client?.shipping_address?.address_line1 && (
                <p>{client.shipping_address.address_line1}</p>
              )}
              {client?.shipping_address?.address_line2 && (
                <p>{client.shipping_address.address_line2}</p>
              )}
              <p>
                {[
                  client?.shipping_address?.city,
                  client?.shipping_address?.state,
                  client?.shipping_address?.postal_code,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              {client?.shipping_address?.country_id && (
                <p>Country ID: {client.shipping_address.country_id}</p>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  const deleteDialogFooter = (
    <div>
      <Button
        className="p-button-text"
        onClick={() => setDeleteDialogVisible(false)}
        disabled={isDeletingClient}
      >
        <FontAwesomeIcon icon={faXmark} /> No
      </Button>
      <Button
        className="p-button-danger"
        onClick={handleDeleteClient}
        isLoading={isDeletingClient}
      >
        <FontAwesomeIcon icon={faCheck} /> Yes
      </Button>
    </div>
  );

  if (!client) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-medium text-gray-700">Client not found</h2>
        <p className="text-gray-600 mt-2">
          The requested client could not be found or you don't have permission
          to view it.
        </p>
        <Button className="mt-4" onClick={() => navigate("/client")}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Toast ref={toast} />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {client.client_name}
        </h1>

        <div className="flex gap-2">
          <Button
            className="p-button-outlined"
            onClick={() => navigate(`/invoice/create?clientId=${id}`)}
          >
            <FontAwesomeIcon icon={faPlus} /> Create Invoice
          </Button>
          <Button onClick={() => navigate(`/client/edit/${id}`)}>
            <FontAwesomeIcon icon={faPencil} /> Edit
          </Button>
          <Button
            className="p-button-danger"
            onClick={() => setDeleteDialogVisible(true)}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="col-span-2">
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h2 className="text-lg font-semibold mb-3">
                  Contact Information
                </h2>
                <div className="space-y-2">
                  {client.primary_contact_person && (
                    <p className="flex items-center">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="text-gray-500 w-5 mr-2"
                      />
                      <span className="font-medium">Primary Contact:</span>{" "}
                      <span className="ml-1">
                        {client.primary_contact_person}
                      </span>
                    </p>
                  )}
                  {client.company_name && (
                    <p className="flex items-center">
                      <span className="w-5 mr-2"></span>
                      <span className="font-medium">Company:</span>{" "}
                      <span className="ml-1">{client.company_name}</span>
                    </p>
                  )}
                  {client.email && (
                    <p className="flex items-center">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="text-gray-500 w-5 mr-2"
                      />
                      <span className="font-medium">Email:</span>{" "}
                      <a
                        href={`mailto:${client.email}`}
                        className="ml-1 text-primary hover:underline"
                      >
                        {client.email}
                      </a>
                    </p>
                  )}
                  {client.phone && (
                    <p className="flex items-center">
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="text-gray-500 w-5 mr-2"
                      />
                      <span className="font-medium">Phone:</span>{" "}
                      <span className="ml-1">{client.phone}</span>
                    </p>
                  )}
                  {client.pan_no && (
                    <p className="flex items-center">
                      <span className="w-5 mr-2"></span>
                      <span className="font-medium">Pan No:</span>{" "}
                      <span className="ml-1">{client.pan_no}</span>
                    </p>
                  )}
                  {client.currency_code && (
                    <p className="flex items-center">
                      <span className="w-5 mr-2"></span>
                      <span className="font-medium">Currency:</span>{" "}
                      <span className="ml-1">{client.currency_code}</span>
                    </p>
                  )}
                  {client.payment_term_id && (
                    <p className="flex items-center">
                      <span className="w-5 mr-2"></span>
                      <span className="font-medium">Payment Term:</span>{" "}
                      <span className="ml-1">{client.payment_term_name}</span>
                    </p>
                  )}
                  {client.business_type_id && (
                    <p className="flex items-center">
                      <span className="w-5 mr-2"></span>
                      <span className="font-medium">GST Treatment:</span>{" "}
                      <span className="ml-1">{client.business_type_name}</span>
                    </p>
                  )}
                  {client.website_url && (
                    <p className="flex items-center">
                      <span className="w-5 mr-2"></span>
                      <span className="font-medium">Website Url:</span>{" "}
                      <span className="ml-1">{client.website_url}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {client.notes && (
              <div className="mt-4">
                <h2 className="text-lg font-semibold mb-2">Notes</h2>
                <p className="text-gray-700">{client.notes}</p>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-3">Financial Summary</h2>
            <div className="space-y-3">
              <div>
                <span className="font-medium">Total Revenue:</span>
                <p className="text-xl font-bold text-primary mt-1">
                  {totalRevenue ? formatCurrency(totalRevenue) : 0}
                </p>
              </div>
              <div>
                <span className="font-medium">Outstanding Balance:</span>
                <p className="text-xl font-bold text-amber-500 mt-1">
                  {outstandingBalance ? formatCurrency(outstandingBalance) : 0}
                </p>
              </div>
              <div>
                <span className="font-medium">Invoice Count:</span>
                <p className="text-xl font-bold text-gray-800 mt-1">
                  {totalRecords || 0}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <TabView
        activeIndex={activeTabIndex}
        onTabChange={(e) => setActiveTabIndex(e.index)}
      >
        <TabPanel header="Invoices">
          <DataTable
            value={invoices}
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows}
            totalRecords={totalRecords}
            onPage={onPage}
            rowsPerPageOptions={[5, 10, 25]}
            emptyMessage="No invoices found for this client"
            responsiveLayout="scroll"
            stripedRows
          >
            <Column
              field="invoice_number"
              header="Invoice #"
              body={invoiceNumberTemplate}
            />
            <Column
              field="issue_date"
              header="Issue Date"
              body={(rowData) => dateTemplate(rowData, "issue_date")}
              sortable
            />
            <Column
              field="due_date"
              header="Due Date"
              body={(rowData) => dateTemplate(rowData, "due_date")}
              sortable
            />
            <Column
              field="status"
              header="Status"
              body={statusTemplate}
            />
            <Column
              field="total"
              header="Amount"
              body={amountTemplate}
              sortable
              style={{ textAlign: "right" }}
            />
            <Column
              body={actionTemplate}
              header="Actions"
              style={{ width: "14rem", textAlign: "center" }}
            />
          </DataTable>
        </TabPanel>

        <TabPanel header="Addresses">{renderAddresses()}</TabPanel>

        <TabPanel header="Contact Persons">{renderContactPersons()}</TabPanel>

        <TabPanel header="Documents">{renderDocuments()}</TabPanel>

        <TabPanel header="Activity">
          <div className="p-4">
            <p className="text-gray-600">
              Activity tracking is available in the Enterprise version.
            </p>
          </div>
        </TabPanel>
      </TabView>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: "450px" }}
        modal
        footer={deleteDialogFooter}
        onHide={() => setDeleteDialogVisible(false)}
        closable={!isDeletingClient}
      >
        <div className="flex align-items-center justify-content-center">
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem", color: "#f59e0b" }}
          />
          <span>
            Are you sure you want to delete client <strong>{client?.client_name}</strong>?
          </span>

        </div>
      </Dialog>
    </div>
  );
};